// Config compartilhada do workflow financeiro + filtro "Filtrar ▾ + chip".
// Status no sistema E (docs/DESIGN.md) = ponto + frase; o tom diz quem age:
// run = andando sozinho, att = precisa de alguém, ok = concluído, bad = travou, off = encerrado.
import { useId, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ListFilter, X } from 'lucide-react';
import { DOT, StatusDot } from '@/components/Page';
import { tomDoPedido } from '@/lib/pagamento';

const cfg = (label, tone) => ({ label, tone, color: DOT[tone] });

export const WORKFLOW_CONFIG = {
  aguardando_pagamento: cfg('Aguardando pagamento', 'run'),
  comprovante_em_analise: cfg('Comprovante em análise', 'att'),
  pagamento_confirmado: cfg('Pagamento confirmado', 'run'),
  rejeitado: cfg('Comprovante rejeitado', 'bad'),
  enviado_tiny: cfg('Enviado ao Tiny', 'ok'),
  erro_tiny: cfg('Erro no Tiny', 'bad'),
  revisao_antifraude: cfg('Revisão antifraude', 'att'),
  cancelado: cfg('Cancelado', 'off'),
};

export const getWorkflowConfig = (wf) => WORKFLOW_CONFIG[wf] || WORKFLOW_CONFIG.aguardando_pagamento;

// Workflow "efetivo" pra exibição/filtro: pagamento já confirmado mas workflow
// preso numa etapa inicial mostra "pagamento_confirmado" (evita "Aguardando" + pago).
export const effectiveWorkflow = (order) => {
  const raw = order?.workflow_status;
  return (order?.payment_status === 'confirmed'
    && ['aguardando_pagamento', 'comprovante_em_analise'].includes(raw))
    ? 'pagamento_confirmado'
    : raw;
};

// `pedido` (opcional): o tom segue o pedido, não só o workflow (financiamento no Tiny e não pago anda, não concluiu).
export const WorkflowChip = ({ wf, pedido = null, className = '' }) => {
  const c = getWorkflowConfig(wf);
  return <StatusDot tone={tomDoPedido(pedido, c.tone)} className={className}>{c.label}</StatusDot>;
};

// Filtro aplicado: chip com o rótulo (e o ponto do status, se tiver) e o X que remove.
const Chip = ({ label, color, onRemove, rotuloRemover }) => (
  <span className="inline-flex items-center gap-2 rounded-lg bg-accent py-1 pl-2.5 pr-1 text-[13px] text-foreground">
    {color && <span className={`h-[7px] w-[7px] rounded-full ${color}`} aria-hidden />}
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="rounded-md p-1 text-gray-500 transition-colors hover:bg-card hover:text-foreground"
      aria-label={rotuloRemover}
    >
      <X className="h-3.5 w-3.5" />
    </button>
  </span>
);

const TITULO_GRUPO = 'px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500';

// Uma opção do popover (a escolhida fica marcada para o leitor de tela). Fica fora do filtro de propósito:
// declarada dentro, cada nova renderização da tela recriaria as linhas e o foco do teclado se perderia.
const OpcaoFiltro = ({ v, label, color, n, atual, aoEscolher }) => (
  <button
    type="button"
    onClick={() => aoEscolher(v)}
    aria-pressed={atual === v}
    className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
      atual === v ? 'bg-accent font-medium text-foreground' : 'text-gray-600 hover:bg-accent dark:text-gray-300'
    }`}
  >
    {color && <span className={`h-[7px] w-[7px] rounded-full ${color}`} aria-hidden />}
    <span className="flex-1">{label}</span>
    {n != null && (
      <span className={`text-xs tabular-nums ${n === 0 ? 'opacity-40' : 'opacity-60'}`}>{n}</span>
    )}
  </button>
);

/**
 * Filtro por status: botão "Filtrar" abre a lista (ponto + contagem); o filtro
 * aplicado vira um chip removível. `counts` é opcional ({all, [wf]: n}).
 * `segundo` (opcional) é mais um grupo no mesmo botão, em outra coluna e com o seu chip:
 * { titulo, opcoes: {valor: {label}}, value, onChange, todos }. Sem cor: a cor é só do status.
 */
const WorkflowStatusFilter = ({ value, onChange, counts = null, config = WORKFLOW_CONFIG, segundo = null }) => {
  const [open, setOpen] = useState(false);
  const idStatus = useId();
  const idSegundo = useId();
  const active = value !== 'all' ? (config[value] || null) : null;
  const ativoSegundo = segundo && segundo.value !== 'all' ? (segundo.opcoes[segundo.value] || null) : null;

  const escolherStatus = (v) => {
    onChange(v);
    setOpen(false);
  };
  const escolherSegundo = (v) => {
    segundo.onChange(v);
    setOpen(false);
  };

  const grupoStatus = (
    <>
      <OpcaoFiltro v="all" label="Todos" color="bg-gray-400" n={counts ? counts.all : null} atual={value} aoEscolher={escolherStatus} />
      <div className="my-1 h-px bg-border" aria-hidden />
      {Object.entries(config).map(([v, c]) => (
        <OpcaoFiltro
          key={v}
          v={v}
          label={c.label}
          color={c.color}
          n={counts ? counts[v] || 0 : null}
          atual={value}
          aoEscolher={escolherStatus}
        />
      ))}
    </>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2" data-testid="status-filter-button">
            <ListFilter className="h-4 w-4" />
            Filtrar
          </Button>
        </PopoverTrigger>
        {segundo ? (
          <PopoverContent
            align="start"
            collisionPadding={16}
            className="max-h-[var(--radix-popover-content-available-height)] w-[min(34rem,calc(100vw_-_2rem))] overflow-y-auto p-1.5"
          >
            <div className="grid gap-y-3 sm:grid-cols-2 sm:gap-x-1.5">
              <div role="group" aria-labelledby={idStatus}>
                <p id={idStatus} className={TITULO_GRUPO}>Status</p>
                {grupoStatus}
              </div>
              <div
                role="group"
                aria-labelledby={idSegundo}
                className="border-t border-border pt-2 sm:border-l sm:border-t-0 sm:pl-1.5 sm:pt-0"
              >
                <p id={idSegundo} className={TITULO_GRUPO}>{segundo.titulo}</p>
                <OpcaoFiltro v="all" label={segundo.todos || 'Todas'} atual={segundo.value} aoEscolher={escolherSegundo} />
                <div className="my-1 h-px bg-border" aria-hidden />
                {Object.entries(segundo.opcoes).map(([v, o]) => (
                  <OpcaoFiltro key={v} v={v} label={o.label} atual={segundo.value} aoEscolher={escolherSegundo} />
                ))}
              </div>
            </div>
          </PopoverContent>
        ) : (
          <PopoverContent align="start" className="w-72 p-1.5">
            {grupoStatus}
          </PopoverContent>
        )}
      </Popover>

      {active && (
        <Chip
          label={active.label}
          color={active.color}
          onRemove={() => onChange('all')}
          rotuloRemover={segundo ? 'Remover filtro de status' : 'Remover filtro'}
        />
      )}
      {ativoSegundo && (
        <Chip
          label={ativoSegundo.label}
          onRemove={() => segundo.onChange('all')}
          rotuloRemover={`Remover filtro de ${segundo.titulo.toLowerCase()}`}
        />
      )}
    </div>
  );
};

export default WorkflowStatusFilter;
